import { useState, useEffect, useCallback } from 'react';
import { Box, Button, Chip, Paper, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useParams } from 'react-router';
import EditAuthorModal from './components/edit-author-modal';
import ListEditButton from '../../common/list-edit-button';
import { useStringsContext } from '../../../contexts/strings';
import { useAuthors } from './hooks/useAuthors';
import { App, Author } from './types';
import { ROLES, SxStyle } from '../../../types';
import { useUserAuth } from '../../../contexts/auth';

const styles: SxStyle = {
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 4,
  },
  container: (theme) => ({
    width: '100%',
    height: 'calc(100% - 60px)',
    marginTop: theme.spacing(4),
  }),
  dataGrid: {
    '.MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '.MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '.MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '.MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: 'shades.p10',
    },
  },
};

function AuthorsList() {
  const params = useParams<{ abbrv: string }>();
  const selectedAbbrv = params.abbrv;
  const { strings } = useStringsContext();
  const [loading, setLoading] = useState(true);
  const { fetchAuthors } = useAuthors();
  const [authors, setAuthors] = useState<Author[]>();
  const [editAuthor, setEditAuthor] = useState<Author | null>(null);

  const { isSuperAdmin, hasTeamRole } = useUserAuth();
  const isReadOnly = !isSuperAdmin && hasTeamRole(selectedAbbrv, ROLES.VIEWER);

  const columns: GridColDef[] = [
    { field: 'name', headerName: strings.articles.fieldNameAuthorName, flex: 1 },
    { field: 'email', headerName: strings.articles.fieldNameAuthorEmail, flex: 1 },
    {
      field: 'apps',
      headerName: strings.articles.fieldNameAppAbbrvs,
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
          {(params.value as App[]).map((t) => (
            <Chip key={t.id} label={t.abbrv} size="small" />
          ))}
        </Stack>
      ),
    },
    {
      field: 'articlesCount',
      headerName: strings.articles.fieldNameAuthorArticleCount,
      width: 120,
      valueGetter: (value, row) => row._count.articles || 0,
    },
    {
      field: 'edit',
      headerName: '',
      minWidth: 60,
      maxWidth: 60,
      align: 'right',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ListEditButton onEdit={() => setEditAuthor(params.row)} isReadOnly={isReadOnly} />
      ),
    },
  ];

  const getAuthors = useCallback(async () => {
    setLoading(true);
    try {
      const results = await fetchAuthors();
      setAuthors(results);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  }, [fetchAuthors]);

  useEffect(() => {
    getAuthors();
  }, [getAuthors]);

  const createAuthor = () => {
    setEditAuthor({
      name: '',
    } as Author);
  };

  return (
    <>
      <Box sx={styles.actions}>
        <Button variant="contained" color="primary" onClick={createAuthor} disabled={isReadOnly}>
          {strings.articles.createAuthor}
        </Button>
      </Box>
      <Paper elevation={4} sx={styles.container}>
        <DataGrid
          loading={loading}
          sx={styles.dataGrid}
          rows={authors || []}
          columns={columns}
          autoPageSize
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'createdAt',
                  sort: 'desc',
                },
              ],
            },
          }}
        />
        {editAuthor && (
          <EditAuthorModal
            onDone={() => {
              setEditAuthor(null);
              getAuthors();
            }}
            author={editAuthor}
            open={!!editAuthor}
            onClose={() => {
              setEditAuthor(null);
              getAuthors();
            }}
          />
        )}
      </Paper>
    </>
  );
}

export default AuthorsList;
